import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    BodyText,
    Button,
    CopyToClipboard,
    Divider,
    FlexContainer,
    Modal,
    MoreInfo,
    Spinner,
} from '@placer-ui/components';
import {
    selectOfflineReportsRequestsComments,
    selectOfflineReportsRequestsCommentsLoading,
    selectSupportSelectedRequest,
} from 'store/offline-reports/selectors/offline-reports-selectors-factory';
import {
    convertSupportStatus,
    isDataTrustRequest,
} from 'features/my-zone/support-requests/utils/utils';
import { tableDateFormatter } from 'utils/table-formatters/table-formatters';
import { SupportRequestComment } from 'features/my-zone/support-requests/components/support-requests-modals/components/support-request-comment/support-request-comment';
import { TitleModalHeader } from 'features/my-zone/support-requests/components/support-requests-modals/components/title-modal-header/title-modal-header';
import { useOfflineReportsActions } from 'store/offline-reports/use-offline-reports-actions';
import styles from './support-requests-info-modal.module.scss';
import { useOpenSupportRequest } from 'features/my-zone/support-requests/components/support-requests-modals/support-modal-manager';

const PLACER_ADDRESS = 'support@placer.ai';

const lastUpdatedTooltipText =
    "Some ticket updates reflect\n internal processes which are not\n visible in the ticket's activity.";

export type InfoRequestProps = {
    requestId: string;
};

export const SupportRequestsInfoModal = ({ requestId }: InfoRequestProps) => {
    const selectedRequest = useSelector(selectSupportSelectedRequest);
    const comments = useSelector(selectOfflineReportsRequestsComments);
    const isLoading = useSelector(selectOfflineReportsRequestsCommentsLoading);
    const { getOfflineReportsRequestsComments } = useOfflineReportsActions();
    const { openSupportRequest } = useOpenSupportRequest();

    useEffect(() => {
        requestId && getOfflineReportsRequestsComments(String(requestId));
    }, [requestId, getOfflineReportsRequestsComments]);

    const attachmentsCount = useMemo(
        () =>
            comments
                .filter(({ author_meta }) => author_meta.fromAddress === PLACER_ADDRESS)
                .map(({ attachments }) => attachments)
                .flat().length,
        [comments],
    );

    if (!selectedRequest) return null;

    const { status, requestedOn, deliveryETA, lastModified, supportTeam, subject } =
        selectedRequest;

    return (
        <>
            <TitleModalHeader title={subject} />
            <Modal.Content>
                <div className={styles.modalBody}>
                    <div className={styles.info}>
                        <BodyText variant="bold">Placer.ai support ticket #:</BodyText>
                        <FlexContainer>
                            <CopyToClipboard testId="support-copy-id-to-clipboard">
                                {requestId}
                            </CopyToClipboard>
                        </FlexContainer>
                        <BodyText variant="bold">Status:</BodyText>
                        <BodyText testId="support-modal-status">
                            {convertSupportStatus(status)}
                        </BodyText>
                        <BodyText variant="bold">Request Date:</BodyText>
                        <BodyText testId="support-modal-request-date">
                            {tableDateFormatter(requestedOn)}
                        </BodyText>
                        {deliveryETA && !isDataTrustRequest(supportTeam) && (
                            <>
                                <BodyText variant="bold">Location Request ETA:</BodyText>
                                <BodyText testId="support-modal-request-eta">
                                    {tableDateFormatter(deliveryETA, false)}
                                </BodyText>
                            </>
                        )}
                        <BodyText variant="bold">Last Updated:</BodyText>
                        <FlexContainer gap={8} alignItems="center">
                            <BodyText testId="support-modal-last-updated">
                                {tableDateFormatter(lastModified)}
                            </BodyText>
                            <MoreInfo
                                tooltip={{
                                    title: lastUpdatedTooltipText,
                                    placement: 'top',
                                }}
                                testId="support-modal-last-updated-icon"
                            />
                        </FlexContainer>
                        {!isLoading && !!attachmentsCount && (
                            <>
                                <BodyText variant="bold">Files from Support:</BodyText>
                                <BodyText>{attachmentsCount}</BodyText>
                            </>
                        )}
                    </div>
                    <Divider />

                    {isLoading ? (
                        <FlexContainer justifyContent="center" alignItems="center" height="260px">
                            <Spinner variant="brand" />
                        </FlexContainer>
                    ) : (
                        <FlexContainer gap={16} flexDirection="column" minHeight="270px">
                            {[...comments].reverse().map((comment) => (
                                <SupportRequestComment comment={comment} key={comment.id} />
                            ))}
                        </FlexContainer>
                    )}
                </div>
            </Modal.Content>
            <Modal.Footer>
                <Modal.Footer.RightControls>
                    <Button
                        testId="support-request-info-modal-submit-button"
                        variant="primary-brand"
                        fullWidth
                        onClick={() => openSupportRequest({ flowType: 'message' })}
                    >
                        Send a Message
                    </Button>
                </Modal.Footer.RightControls>
            </Modal.Footer>
        </>
    );
};
